import { Controller } from "@hotwired/stimulus"
import { readShareParams, getBaseCalculatorPath } from "../modules/salary_lab/url_state"
import { trackShare } from "../modules/salary_lab/ga4_tracker"

export default class extends Controller {
  static targets = ["webShareButton", "feedback"]
  static values = { calculatorType: String, permalinkUrl: String }

  connect() {
    if (typeof navigator.share === "function") {
      this.webShareButtonTarget.classList.remove("hidden")
    }
  }

  async copyLink() {
    const url = await this._ensureShareUrl()

    try {
      await navigator.clipboard.writeText(url)
    } catch {
      this._fallbackCopy(url)
    }
    this._showFeedback("Link copied!")
    trackShare(this.calculatorTypeValue, "copy_link")
  }

  async openFacebook() {
    const url = await this._ensureShareUrl()
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    this._openWindow(shareUrl, "facebook")
    trackShare(this.calculatorTypeValue, "facebook")
  }

  async openLinkedIn() {
    const url = await this._ensureShareUrl()
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    this._openWindow(shareUrl, "linkedin")
    trackShare(this.calculatorTypeValue, "linkedin")
  }

  async openWhatsApp() {
    const url = await this._ensureShareUrl()
    const text = this._buildShareText()
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`
    this._openWindow(shareUrl, "whatsapp")
    trackShare(this.calculatorTypeValue, "whatsapp")
  }

  async openX() {
    const url = await this._ensureShareUrl()
    const text = this._buildShareText()
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    this._openWindow(shareUrl, "x")
    trackShare(this.calculatorTypeValue, "x")
  }

  async openWebShare() {
    const url = await this._ensureShareUrl()
    const text = this._buildShareText()

    try {
      await navigator.share({ title: document.title, text, url })
      this._showFeedback("Shared!")
      trackShare(this.calculatorTypeValue, "web_share")
    } catch (err) {
      if (err.name !== "AbortError") {
        this._showFeedback("Share failed")
      }
    }
  }

  // --- Private ---

  async _ensureShareUrl() {
    if (this.hasPermalinkUrlValue && this.permalinkUrlValue) {
      return this.permalinkUrlValue
    }

    const currentParams = window.location.search
    if (this._shareUrlPromise && this._cachedForParams === currentParams) {
      return this._shareUrlPromise
    }

    this._cachedForParams = currentParams
    this._shareUrlPromise = this._generateShareUrl().catch(() => {
      this._shareUrlPromise = null
      this._cachedForParams = null
      return window.location.href
    })

    return this._shareUrlPromise
  }

  async _generateShareUrl() {
    if (!this.hasCalculatorTypeValue || !this.calculatorTypeValue) {
      return window.location.href
    }

    const calcType = this.calculatorTypeValue
    const params = readShareParams(calcType)
    const body = { type: calcType, ...params }

    const basePath = getBaseCalculatorPath().replace(/\/+$/, "")
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(`${basePath}/results/share`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) throw new Error(`Share failed: ${response.status}`)

    const data = await response.json()
    return data.url
  }

  _buildShareText() {
    const calcType = this.calculatorTypeValue
    const sp = new URLSearchParams(window.location.search)

    switch (calcType) {
      case "paye": {
        const salary = sp.get("s") || sp.get("salary")
        return salary
          ? `Check what $${Number(salary).toLocaleString()} actually means after tax in NZ`
          : "Check what your salary means after tax in NZ"
      }
      case "contractor": {
        const rate = sp.get("rate")
        return rate
          ? `What does a contractor on $${rate}/hr actually take home in NZ?`
          : "What does a contractor actually take home in NZ?"
      }
      case "reverse": {
        const net = sp.get("net")
        return net
          ? `Want $${Number(net).toLocaleString()} take-home in NZ? Here's the gross salary you need`
          : "Find out what gross salary you need for your target take-home pay in NZ"
      }
      case "comparison":
        return "Compare two NZ salary scenarios side-by-side"
      case "employer_cost":
        return "See the true cost of employing someone in NZ"
      case "kiwisaver":
        return "See how your KiwiSaver could grow over time"
      case "student_loan":
        return "Find out when you'll pay off your student loan in NZ"
      case "minimum_wage":
        return "See what minimum wage means for your take-home pay in NZ"
      case "cost_of_living":
        return "Compare the cost of living between NZ cities"
      case "budget_planner":
        return "Plan your budget based on your NZ take-home pay"
      default:
        return "Check out this NZ salary calculator"
    }
  }

  _openWindow(url, name) {
    const width = 600
    const height = 500
    const left = (screen.width - width) / 2
    const top = (screen.height - height) / 2
    window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`)
  }

  _fallbackCopy(text) {
    const input = document.createElement("input")
    input.value = text
    document.body.appendChild(input)
    input.select()
    document.execCommand("copy")
    document.body.removeChild(input)
  }

  _showFeedback(text) {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.textContent = text
    this.feedbackTarget.classList.remove("hidden")
    clearTimeout(this._feedbackTimer)
    this._feedbackTimer = setTimeout(() => {
      this.feedbackTarget.classList.add("hidden")
    }, 2000)
  }
}
